// Shared immersive video player + engagement modal.
// Extracted from VenueProfile so the listing (VideoGallery) and the showcase
// (FilmsSection) use ONE player with the same rating/engagement experience.
import { useState, useRef, useEffect } from 'react';

const FD = "var(--font-heading-primary)";
const FB = "var(--font-body)";

function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= bp);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(`(max-width: ${bp}px)`);
    const fn = (e) => setMobile(e.matches);
    mql.addEventListener("change", fn);
    return () => mql.removeEventListener("change", fn);
  }, [bp]);
  return mobile;
}

export default function VideoPlayModal({ video, videos = [], onSelect, onClose, engagement }) {
  const isMobile = useIsMobile();
  const [hovPrev, setHovPrev] = useState(false);
  const [hovNext, setHovNext] = useState(false);
  const [copied, setCopied]   = useState(false);
  const [likedMap, setLikedMap]       = useState({});
  const [commentsMap, setCommentsMap] = useState({});
  const [commentText, setCommentText] = useState("");
  const [ytPaused, setYtPaused]       = useState(false);
  const [resolvedEmbedUrl, setResolvedEmbedUrl] = useState(null);

  const idx = videos.findIndex((v) => v.id === video.id);
  const hasPrev = idx > 0;
  const hasNext = idx < videos.length - 1;
  const vg = video.videographer;
  const eng = engagement?.[video.id] || { likes: 0, comments: [] };
  const isLiked = likedMap[video.id] || false;
  const likeCount = eng.likes + (isLiked ? 1 : 0);
  const allComments = [...(eng.comments || []), ...(commentsMap[video.id] || [])];

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const embedUrl = video.youtubeId
    ? `https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1&iv_load_policy=3&showinfo=0&controls=1&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(origin)}`
    : video.vimeoId
    ? `https://player.vimeo.com/video/${video.vimeoId}?autoplay=1&title=0&byline=0&portrait=0&dnt=1${video.vimeoHash ? `&h=${video.vimeoHash}` : ''}`
    : null;

  // For Vimeo URLs without a numeric ID (e.g. vimeo.com/user/slug), resolve via oEmbed
  const isVimeoUrl = !video.youtubeId && !video.vimeoId && video.url?.includes('vimeo.com');
  useEffect(() => {
    if (!isVimeoUrl || !video.url) return;
    setResolvedEmbedUrl(null);
    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(video.url)}&autoplay=1`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.video_id) return;
        const hash = data.uri?.split('/').find(p => /^[a-f0-9]{8,}$/i.test(p)) || null;
        const url = `https://player.vimeo.com/video/${data.video_id}?autoplay=1&title=0&byline=0&portrait=0&dnt=1${hash ? `&h=${hash}` : ''}`;
        setResolvedEmbedUrl(url);
      })
      .catch(() => {});
  }, [video.url, isVimeoUrl]);

  const activeEmbedUrl = embedUrl || resolvedEmbedUrl;

  const iframeRef = useRef(null);

  // Reset paused overlay and resolved URL when video changes
  useEffect(() => { setYtPaused(false); setResolvedEmbedUrl(null); }, [video.id]);

  // Stop video playback on unmount (prevents audio continuing after modal closes)
  useEffect(() => {
    const iframe = iframeRef.current;
    return () => {
      try {
        if (iframe) {
          // pauseVideo (enablejsapi) stops playback. We deliberately do NOT clear
          // iframe.src here: under React StrictMode the mount→cleanup→mount cycle
          // would blank the src on the live player. Pause is sufficient.
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*'
          );
        }
      } catch (_) {}
    };
  }, []);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onSelect?.(videos[idx - 1]);
      if (e.key === "ArrowRight" && hasNext) onSelect?.(videos[idx + 1]);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, hasPrev, hasNext, idx, videos, onSelect]);

  // Listen for YouTube video end → auto-play next
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !video.youtubeId) return;

    // Tell YouTube we're listening for events
    const onLoad = () => {
      try {
        iframe.contentWindow.postMessage(JSON.stringify({ event: "listening" }), "*");
        iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onStateChange"] }), "*");
      } catch (_) {}
    };
    iframe.addEventListener("load", onLoad);

    // Listen for state change messages from YouTube
    const onMessage = (e) => {
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (data.event === "onStateChange") {
          // YouTube state: 0=ended, 1=playing, 2=paused
          if (data.info === 0 && hasNext) {
            onSelect?.(videos[idx + 1]);
          }
          setYtPaused(data.info === 2 || data.info === 0);
          if (data.info === 1) setYtPaused(false);
        }
      } catch (_) {}
    };
    window.addEventListener("message", onMessage);
    return () => {
      iframe.removeEventListener("load", onLoad);
      window.removeEventListener("message", onMessage);
    };
  }, [video.youtubeId, hasNext, idx, videos, onSelect]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href + `#film-${video.id}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Video, ${video.title}`);
    const body = encodeURIComponent(`Check out this film:\n${video.title}\n\nFilmed by: ${vg?.name || "Unknown"}\n${window.location.href}#film-${video.id}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  // Share via Facebook
  const handleFacebookShare = () => {
    const url = encodeURIComponent(window.location.href + `#film-${video.id}`);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank", "width=600,height=400");
  };

  // Share via Instagram (copy link + open Instagram)
  const handleInstagramShare = () => {
    navigator.clipboard.writeText(`${video.title}, Filmed by ${vg?.name || ""}\n${window.location.href}#film-${video.id}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      window.open("https://www.instagram.com/", "_blank");
    });
  };

  // Share via Pinterest
  const handlePinterestShare = () => {
    const url = encodeURIComponent(window.location.href + `#film-${video.id}`);
    const media = encodeURIComponent(video.thumb);
    const desc = encodeURIComponent(`${video.title}, Filmed by ${vg?.name || ""}`);
    window.open(`https://pinterest.com/pin/create/button/?url=${url}&media=${media}&description=${desc}`, "_blank", "width=600,height=400");
  };

  const navBtn = (dir, enabled, hov, setHov) => (
    <button
      onClick={() => enabled && onSelect?.(videos[dir === "prev" ? idx - 1 : idx + 1])}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      aria-label={dir === "prev" ? "Previous video" : "Next video"}
      style={{
        position: "absolute", top: "50%", transform: "translateY(-50%)",
        [dir === "prev" ? "left" : "right"]: -52,
        width: 40, height: 40, borderRadius: "50%",
        background: hov ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.06)",
        border: `1px solid ${hov ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.12)"}`,
        color: enabled ? (hov ? "#fff" : "rgba(255,255,255,0.6)") : "rgba(255,255,255,0.15)",
        cursor: enabled ? "pointer" : "default",
        fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s",
      }}
    >
      {dir === "prev" ? "‹" : "›"}
    </button>
  );

  const shareBtn = (label, icon, onClick) => (
    <button onClick={onClick}
      style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
        padding: "7px 0", background: "none",
        border: "1px solid rgba(255,255,255,0.1)", borderRadius: "var(--lwd-radius-input)",
        color: "rgba(255,255,255,0.5)", fontFamily: FB, fontSize: 10,
        fontWeight: 600, letterSpacing: "0.4px", cursor: "pointer",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; e.currentTarget.style.color = "#fff"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
    >
      <span style={{ fontSize: 12 }}>{icon}</span> {label}
    </button>
  );

  return (
    <div
      onClick={onClose}
      onMouseDown={e => { if (e.target.tagName === "BUTTON" || e.target.closest("button")) e.preventDefault(); }}
      role="dialog" aria-modal="true" aria-label={`Play ${video.title}`}
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "rgba(0,0,0,0.96)",
        display: "flex", flexDirection: "column",
      }}
    >
      {/* Top bar */}
      <div onClick={(e) => e.stopPropagation()} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontFamily: FD, fontSize: 16, color: "#f5f2ec" }}>
            {idx + 1} <span style={{ color: "rgba(255,255,255,0.3)" }}>/ {videos.length}</span>
          </span>
          {vg && <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.35)" }}>🎬 {vg.name}</span>}
        </div>
        <button onClick={onClose} aria-label="Close video"
          style={{
            background: "none", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "var(--lwd-radius-input)",
            color: "rgba(255,255,255,0.55)", width: 34, height: 34, cursor: "pointer",
            fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s", flexShrink: 0,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#d39a21"; e.currentTarget.style.color = "#d39a21"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}
        >✕</button>
      </div>

      {/* Main content: video + info panel */}
      <div onClick={(e) => e.stopPropagation()} style={{
        flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row",
        minHeight: 0, overflow: isMobile ? "auto" : "hidden",
      }}>
        {/* Video area */}
        <div style={{ flex: isMobile ? "none" : 1, display: "flex", flexDirection: "column", minWidth: 0, position: "relative" }}>
          {/* Prev / Next arrows, hidden on mobile */}
          {!isMobile && videos.length > 1 && navBtn("prev", hasPrev, hovPrev, setHovPrev)}
          {!isMobile && videos.length > 1 && navBtn("next", hasNext, hovNext, setHovNext)}

          {/* Video player */}
          {activeEmbedUrl ? (
            <div style={{
              flex: isMobile ? "none" : 1,
              position: "relative",
              background: "#000",
              ...(isMobile ? { width: "100%", aspectRatio: "16/9" } : {}),
            }}>
              <iframe
                ref={iframeRef}
                key={activeEmbedUrl + video.id}
                src={activeEmbedUrl}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={video.title}
              />
              {/* Overlay to block YouTube "More videos" suggestions when paused */}
              {ytPaused && (
                <div
                  onClick={() => {
                    // Resume playback via YouTube postMessage API
                    try {
                      iframeRef.current?.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
                      setYtPaused(false);
                    } catch (_) {}
                  }}
                  style={{
                    position: "absolute", bottom: 52, left: 0, right: 0,
                    height: "calc(100% - 100px)", zIndex: 2,
                    background: "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.85) 40%, transparent 100%)",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
                    paddingBottom: 40, cursor: "pointer",
                    transition: "opacity 0.3s",
                  }}
                >
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    border: "1px solid rgba(255,255,255,0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
                    marginBottom: 12, transition: "all 0.2s",
                  }}>
                    <span style={{ fontSize: 20, color: "#fff", marginLeft: 3 }}>▶</span>
                  </div>
                  <div style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.5)", letterSpacing: "1px", textTransform: "uppercase" }}>Click to resume</div>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              flex: isMobile ? "none" : 1,
              position: "relative", background: "#000", display: "flex", alignItems: "center", justifyContent: "center",
              ...(isMobile ? { width: "100%", aspectRatio: "16/9" } : {}),
            }}>
              <img src={video.thumb} alt={video.title} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.2 }} />
              <div style={{ position: "relative", textAlign: "center", padding: 32 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%", margin: "0 auto 14px",
                  border: "1px solid rgba(211,154,33,0.35)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(211,154,33,0.08)",
                }}>
                  <span style={{ fontSize: 18, color: "#d39a21", marginLeft: 3 }}>▶</span>
                </div>
                <div style={{ fontFamily: FD, fontSize: 15, color: "rgba(211,154,33,0.85)", letterSpacing: "1.5px" }}>
                  {isVimeoUrl ? "Loading film…" : "Film Coming Soon"}
                </div>
                <div style={{ fontFamily: FB, fontSize: 12, color: "rgba(255,255,255,0.4)", maxWidth: 300, lineHeight: 1.65, marginTop: 8 }}>
                  {isVimeoUrl ? "Connecting to Vimeo…" : <>This film will be available shortly.<br />Contact us for a private screening enquiry.</>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Info panel (right on desktop, below on mobile) ── */}
        <div style={{
          width: isMobile ? "100%" : 280, flexShrink: 0,
          borderLeft: isMobile ? "none" : "1px solid rgba(255,255,255,0.06)",
          borderTop: isMobile ? "1px solid rgba(255,255,255,0.06)" : "none",
          background: "rgba(10,8,6,0.6)", backdropFilter: "blur(8px)",
          display: "flex", flexDirection: "column",
          overflowY: "auto", padding: isMobile ? "16px 16px 40px" : "20px 18px",
        }}>
          {/* Title + type */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: FD, fontSize: isMobile ? 19 : 17, color: "#f5f2ec", lineHeight: 1.3, marginBottom: 4 }}>{video.title}</div>
            <div style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
              {video.duration} · {video.type === "wedding" ? "Wedding Film" : video.type === "tour" ? "Estate Tour" : "Highlights"}
            </div>
          </div>

          {/* Description */}
          {video.desc && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>About This Film</div>
              <div style={{ fontFamily: FB, fontSize: 12, color: "rgba(255,255,255,0.65)", lineHeight: 1.65, fontWeight: 300 }}>{video.desc}</div>
            </div>
          )}

          {/* Videographer details */}
          {vg && (
            <div style={{
              marginBottom: 18, padding: "14px 12px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "var(--lwd-radius-card)",
            }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>Videographer</div>
              <div style={{ fontFamily: FD, fontSize: 16, color: "#f5f2ec", marginBottom: 3 }}>{vg.name}</div>
              <div style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontSize: 11, opacity: 0.6 }}>📍</span> {vg.area}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {vg.instagram && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>📷</span>
                    <span style={{ fontFamily: FB, fontSize: 11, color: "#d39a21" }}>{vg.instagram}</span>
                  </div>
                )}
                {vg.website && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>🌐</span>
                    <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{vg.website}</span>
                  </div>
                )}
                {vg.camera && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, opacity: 0.5 }}>🎥</span>
                    <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{vg.camera}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Tags</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {video.tags.map(t => (
                  <span key={t} style={{
                    fontFamily: FB, fontSize: 10, color: "rgba(255,255,255,0.55)",
                    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)",
                    padding: "3px 8px", borderRadius: "var(--lwd-radius-input)", letterSpacing: "0.3px",
                  }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Sharing */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Share</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {shareBtn(copied ? "Copied!" : "Copy", "📋", handleCopyLink)}
              {shareBtn("Pin", "📌", handlePinterestShare)}
              {shareBtn("Email", "✉", handleEmailShare)}
              {shareBtn("Facebook", "📘", handleFacebookShare)}
              {shareBtn("Instagram", "📷", handleInstagramShare)}
            </div>
          </div>

          {/* ── Appreciations & Reflections ── */}
          <div style={{ marginBottom: 18 }}>
            {/* Appreciation + reflection count */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
              <button onClick={() => setLikedMap(m => ({ ...m, [video.id]: !m[video.id] }))}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: isLiked ? "rgba(211,154,33,0.08)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${isLiked ? "rgba(211,154,33,0.25)" : "rgba(255,255,255,0.06)"}`,
                  borderRadius: "var(--lwd-radius-input)", padding: "6px 12px", cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <span style={{ fontSize: 13, transition: "transform 0.2s", transform: isLiked ? "scale(1.15)" : "scale(1)" }}>{isLiked ? "❤️" : "🤍"}</span>
                <span style={{ fontFamily: FB, fontSize: 10, fontWeight: 600, color: isLiked ? "#d39a21" : "rgba(255,255,255,0.45)", letterSpacing: "0.3px" }}>
                  {likeCount} {likeCount === 1 ? "Appreciation" : "Appreciations"}
                </span>
              </button>
              {allComments.length > 0 && (
                <span style={{ fontFamily: FB, fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: "0.3px" }}>
                  {allComments.length} {allComments.length === 1 ? "Reflection" : "Reflections"}
                </span>
              )}
            </div>

            {/* Reflection input */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.25)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Share Your Reflection</div>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Share your reflection on this film..."
                style={{
                  width: "100%", minHeight: 52, resize: "vertical",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "var(--lwd-radius-input)", padding: "10px 12px",
                  fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.65)",
                  lineHeight: 1.6, outline: "none", fontStyle: "italic",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(211,154,33,0.3)"; e.currentTarget.style.fontStyle = "normal"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; if (!commentText) e.currentTarget.style.fontStyle = "italic"; }}
              />
              {commentText.trim() && (
                <button
                  onClick={() => {
                    if (!commentText.trim()) return;
                    setCommentsMap(m => ({
                      ...m,
                      [video.id]: [...(m[video.id] || []), { name: "You", text: commentText.trim(), date: new Date().toISOString().slice(0, 10) }],
                    }));
                    setCommentText("");
                  }}
                  style={{
                    marginTop: 6, width: "100%", padding: "8px 0",
                    background: "rgba(211,154,33,0.06)", border: "1px solid rgba(211,154,33,0.2)",
                    borderRadius: "var(--lwd-radius-input)", cursor: "pointer",
                    fontFamily: FB, fontSize: 10, fontWeight: 600,
                    color: "#d39a21", letterSpacing: "0.8px", textTransform: "uppercase",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.06)"; }}
                >
                  Share Reflection
                </button>
              )}
            </div>

            {/* Curated Reflections */}
            {allComments.length > 0 && (
              <div>
                <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.25)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
                  Curated Reflections
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 180, overflowY: "auto" }}>
                  {allComments.map((c, ci) => (
                    <div key={ci} style={{
                      padding: "10px 12px",
                      borderLeft: "2px solid rgba(211,154,33,0.25)",
                      background: "rgba(255,255,255,0.015)",
                    }}>
                      <div style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.6, fontStyle: "italic", fontWeight: 300 }}>
                        "{c.text}"
                      </div>
                      <div style={{ fontFamily: FB, fontSize: 10, color: "rgba(211,154,33,0.5)", marginTop: 6, fontWeight: 600 }}>
                       , {c.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Film meta */}
          <div style={{
            marginTop: "auto", paddingTop: 14,
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}>
            <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.25)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>Film Info</div>
            <div style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.7 }}>
              Film {idx + 1} of {videos.length}<br />
              {vg ? `© ${vg.name}` : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      <div onClick={(e) => e.stopPropagation()} style={{
        flexShrink: 0, borderTop: "1px solid rgba(255,255,255,0.06)",
        padding: isMobile ? "8px 8px" : "8px 12px", overflow: "hidden",
      }}>
        <div style={{ display: "flex", gap: 6, justifyContent: isMobile ? "flex-start" : "center", overflowX: isMobile ? "auto" : "visible", scrollbarWidth: "none" }}>
          {videos.map((v, i) => (
            <div
              key={v.id}
              onClick={() => onSelect?.(v)}
              style={{
                width: isMobile ? 90 : 120, height: isMobile ? 52 : 68,
                flexShrink: 0, cursor: "pointer",
                border: v.id === video.id ? "2px solid #d39a21" : "2px solid transparent",
                opacity: v.id === video.id ? 1 : 0.4,
                transition: "all 0.2s", overflow: "hidden", position: "relative",
                borderRadius: isMobile ? 2 : 0,
              }}
              onMouseEnter={(e) => { if (v.id !== video.id) e.currentTarget.style.opacity = "0.7"; }}
              onMouseLeave={(e) => { if (v.id !== video.id) e.currentTarget.style.opacity = "0.4"; }}
            >
              <img src={v.thumb} alt={v.title}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              {v.id === video.id && (
                <div style={{
                  position: "absolute", bottom: 3, left: 0, right: 0, textAlign: "center",
                  fontFamily: FB, fontSize: 7, fontWeight: 700, letterSpacing: "0.8px",
                  textTransform: "uppercase", color: "#d39a21",
                  textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                }}>Playing</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
